import { useEffect, useRef } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import dynamic from "next/dynamic";
import { useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { Doc } from "@/components/doc/Doc";
import { DocPlaceholder } from "@/components/doc/DocPlaceholder";
import FloatingChatInput from "@/components/doc/FloatingChatInput";
import { LoadingDocument } from "@/components/doc/LoadingDocument";
import { RightSidebar } from "@/components/doc/right-sidebar/RightSidebar";
import { ImageUrlProvider } from "@/components/tiptap/context/ImageUrlContext";
import { useStore } from "@/store";
import { SCREEN_SM } from "@/style/breakpoints";
import { trackEvent } from "@/utils/trackEvent";
import { useFeatureFlag } from "@/utils/useFeatureFlag";
import { useHandlePermissionQuery } from "@/utils/useHandlePermissionQuery";
import { setLastDocIdForTeam, useLastDocId } from "@/utils/useLastDocId";
import { useOpenChatFromQuery } from "@/utils/useOpenChatFromQuery";

import type { DocumentModel } from "@/models/DocumentModel";
import type { UseDocError } from "@/utils/useDoc";

const DocumentDialogs = dynamic(
  () => import("./DocumentDialogs").then((m) => ({ default: m.DocumentDialogs })),
  { ssr: false },
);

export const DocumentPage = observer(function DocumentPage({
  doc,
  error,
  docId,
  ssrHtml,
}: {
  doc?: DocumentModel;
  error?: UseDocError;
  docId?: string;
  /** The server-rendered document, shown until the Yjs document syncs. */
  ssrHtml?: string;
}) {
  const t = useTranslations();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { documentTree } = useStore();
  const { setLastDocId } = useLastDocId();
  const { teams } = useStore();
  const router = useRouter();
  const pathname = usePathname();

  const refOnClickEnabled = useFeatureFlag("computed-nodes-ref-on-click");

  useHandlePermissionQuery();
  useOpenChatFromQuery(docId);

  useEffect(() => {
    if (!doc) return;
    runInAction(() => {
      doc.computedNodes.refOnClickEnabled = !!refOnClickEnabled;
    });
  }, [doc, refOnClickEnabled]);

  useEffect(() => {
    if (doc) {
      setLastDocId(doc.id);
      if (doc.team?.uid && doc.id && doc.team?.uid === documentTree.currentTeam?.uid) {
        setLastDocIdForTeam(doc.team?.uid, doc.id);
      }

      // Scroll to top on mobile when document loads to ensure title is visible
      // Mobile has a fixed header that can hide the title if page isn't at top
      if (typeof window !== "undefined" && window.innerWidth < SCREEN_SM) {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
    }
  }, [doc, setLastDocId, documentTree.currentTeam]);

  useEffect(() => {
    // If doc is loaded
    if (doc && !error) {
      const matchedTeam = teams.userTeams.find((userTeam) => userTeam.uid === doc.team?.uid);
      if (matchedTeam) {
        // set the current team to the team of the doc
        teams.setCurrentTeam(matchedTeam);
      }
    }
  }, [doc, error, teams, router, pathname]);

  useEffect(() => {
    if (doc && !error && docId) {
      trackEvent("document_loaded", { doc_id: docId });
    } else if (error?.type === "noReadPermissions" && docId) {
      trackEvent("document_access_denied", { doc_id: docId });
    }
    if ((doc || error) && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [doc, error, docId]);

  useEffect(() => {
    if (!doc && !error && docId) {
      timeoutRef.current = setTimeout(() => {
        trackEvent("document_loading_slow", {
          doc_id: docId,
        });
      }, 10_000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [doc, error, docId]);

  // prevents accidental file drops outside of editor or side panels
  useEffect(() => {
    const handleFileDragover = (event: DragEvent) => {
      // this makes window an acceptable target for drop event
      event.preventDefault();
    };
    const handleFileDrop = (event: DragEvent) => {
      event.preventDefault();
    };

    window.addEventListener("drop", handleFileDrop);
    window.addEventListener("dragover", handleFileDragover);

    return () => {
      window.removeEventListener("drop", handleFileDrop);
      window.removeEventListener("dragover", handleFileDragover);
    };
  }, []);

  if (error) {
    let errorTitle = "";
    let errorDescription = "";
    switch (error.type) {
      case "noReadPermissions":
        break;
      case "invalidSchemaVersion":
        errorTitle = t("doc.errors.invalidSchemaVersion");
        errorDescription = error.message;
        break;
      case "networkError":
        errorTitle = error.message;
        break;
    }

    return (
      <div className="relative flex flex-1 flex-row">
        {error.type === "noReadPermissions" ? (
          <DocPlaceholder key={doc?.team?.permalink} team={doc?.team} />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center">
            {errorTitle}
            {errorDescription && <p className="text-sm mt-4 text-gray-500">{errorDescription}</p>}
          </div>
        )}
        <RightSidebar doc={doc} />
        <DocumentDialogs doc={doc} />
      </div>
    );
  }

  if (!doc) {
    return <LoadingDocument ssrHtml={ssrHtml} />;
  }

  // doc is isolated so that awareness cursors don't overlap the sidebars
  return (
    <ImageUrlProvider>
      <div className="relative flex flex-1 flex-row">
        <div className="relative flex flex-1 isolate flex-col">
          <Doc doc={doc} />
          <FloatingChatInput />
        </div>
        <RightSidebar doc={doc} />
        <DocumentDialogs doc={doc} />
      </div>
    </ImageUrlProvider>
  );
});
